import React, { useEffect, useRef, useState } from 'react'
import { DownOutlined } from '@ant-design/icons'
import { Button, Checkbox } from '@surecompanies/core_components'

import { ALL_PAYMENT_SOURCES, PAYMENT_TYPE_OPTIONS } from './helpers'
import styles from './PaymentTypeFilter.module.scss'

/**
 * Payment Type filter — a "Select all + Apply" dropdown matching the design
 * mock, rather than an antd `Select mode="multiple"` that applies live like
 * the other filter-bar controls. Draft semantics: opening the panel seeds a
 * local draft from the currently-APPLIED `selectedSources`; checkbox/Select-all
 * clicks only mutate the draft; Apply commits it via `onApply` (which the
 * caller wires to the hook's `onSourcesChange`, already resetting page to 1);
 * closing without Apply (outside-click or re-click) discards the draft.
 * Outside-click handling modeled on BenefitsTable/ColumnsMenu.tsx.
 */

interface PaymentTypeFilterProps {
  /** Currently applied selection — the source of truth for the query/URL. */
  selectedSources: string[]
  onApply: (sources: string[]) => void
}

const summaryLabel = (selected: string[]): string => {
  if (selected.length === PAYMENT_TYPE_OPTIONS.length) return 'All types'
  if (selected.length === 0) return 'No types'
  if (selected.length === 1) {
    return (
      PAYMENT_TYPE_OPTIONS.find((o) => o.source === selected[0])?.label ??
      selected[0]
    )
  }
  return `${selected.length} types`
}

const PaymentTypeFilter: React.FC<PaymentTypeFilterProps> = ({
  selectedSources,
  onApply,
}) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(selectedSources)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Close (and implicitly discard the draft — it's just local state) on any
  // click outside the trigger + panel.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev
      // Re-seed the draft from the applied selection every time the panel
      // opens, so a discarded draft never leaks into the next open.
      if (next) setDraft(selectedSources)
      return next
    })
  }

  const toggleSource = (source: string) => {
    setDraft((prev) =>
      prev.includes(source)
        ? prev.filter((s) => s !== source)
        : [...prev, source]
    )
  }

  const allSelected = draft.length === PAYMENT_TYPE_OPTIONS.length
  const toggleSelectAll = () => {
    setDraft(allSelected ? [] : ALL_PAYMENT_SOURCES.slice())
  }

  const handleApply = () => {
    onApply(draft)
    setOpen(false)
  }

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        className={styles.trigger}
        onClick={toggleOpen}
        aria-expanded={open}
      >
        <span className={styles.triggerLabel}>
          {summaryLabel(selectedSources)}
        </span>
        <DownOutlined className={styles.chevron} />
      </button>

      {open && (
        <div className={styles.panel}>
          <Checkbox
            className={styles.row}
            checked={allSelected}
            onChange={toggleSelectAll}
          >
            Select all
          </Checkbox>

          <div className={styles.divider} />

          <div className={styles.list}>
            {PAYMENT_TYPE_OPTIONS.map((option) => (
              <Checkbox
                key={option.source}
                className={styles.row}
                checked={draft.includes(option.source)}
                onChange={() => toggleSource(option.source)}
              >
                {option.label}
              </Checkbox>
            ))}
          </div>

          <div className={styles.footer}>
            <Button type="primary" size="small" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentTypeFilter
